import React, { useState } from "react";
import { Button, Input, Typography, Divider, Space, Spin } from "antd";
import {
  GoogleOutlined,
  AppleOutlined,
  LinkedinOutlined,
  MailOutlined,
  SafetyOutlined,
  WindowsOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../../store/context/theme-context";
import { useNotification } from "../../store/context/notification-context";
import { useTenant } from "../../store/context/tenant-context";
import { useApiRequests } from "../../utils/api/https/executor";
import LanguageSelector from "../../shared/languages/LanguageSelector";
import TenantSwitcher from "../../components/General/TenantSwitcher";

const { Title, Text, Link } = Typography;

// SSO Provider configurations
const SSO_PROVIDERS = {
  google: {
    name: "Google",
    icon: <GoogleOutlined style={{ fontSize: "20px" }} />,
    color: "#4285F4",
    authUrl: "/api/auth/google", // Backend OAuth endpoint
  },
  microsoft: {
    name: "Outlook",
    icon: <WindowsOutlined style={{ fontSize: "20px" }} />,
    color: "#00A4EF",
    authUrl: "/api/auth/microsoft",
  },
  apple: {
    name: "Apple",
    icon: <AppleOutlined style={{ fontSize: "20px" }} />,
    color: "#000000",
    authUrl: "/api/auth/apple",
  },
  linkedin: {
    name: "LinkedIn",
    icon: <LinkedinOutlined style={{ fontSize: "20px" }} />,
    color: "#0077B5",
    authUrl: "/api/auth/linkedin",
  },
  sso: {
    name: "SSO",
    icon: <SafetyOutlined style={{ fontSize: "20px" }} />,
    color: "#52c41a",
    authUrl: "/api/auth/sso",
  },
};

const SSOLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tenant = useTenant();
  const { theme } = useTheme();
  const { triggerNotification } = useNotification();
  const { loginUser, loginAdmin } = useApiRequests();

  const isDark = theme === "dark";
  const isAdmin = location.pathname.includes("/admin");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProvider, setLoadingProvider] = useState(null);
  const [showPasswordStep, setShowPasswordStep] = useState(false);

  // Handle SSO provider login
  const handleSSOLogin = async (provider) => {
    try {
      setLoadingProvider(provider);
      
      // Check if backend OAuth is configured
      const authConfig = SSO_PROVIDERS[provider];
      
      // TODO: Remove this check once backend is implemented
      // For now, show a helpful message
      triggerNotification({
        type: "info",
        message: "SSO Not Yet Configured",
        description: `${authConfig.name} login requires backend OAuth configuration. Please use email login for now or contact your administrator.`,
        duration: 5,
      });
      setLoadingProvider(null);
      return;
      
      // UNCOMMENT THE CODE BELOW ONCE BACKEND IS READY:
      /*
      // Store return URL and admin flag in sessionStorage
      sessionStorage.setItem("auth_return_url", location.state?.from || (isAdmin ? "/admin" : "/dashboard"));
      sessionStorage.setItem("auth_is_admin", isAdmin.toString());
      
      // Redirect to OAuth provider
      window.location.href = `${authConfig.authUrl}?tenant=${tenant.tenantId}&returnUrl=${encodeURIComponent(window.location.origin)}/auth/callback`;
      */
      
    } catch (error) {
      console.error("SSO login error:", error);
      triggerNotification({
        type: "error",
        message: "SSO Login Failed",
        description: "Unable to connect to the authentication provider. Please try again.",
      });
      setLoadingProvider(null);
    }
  };

  // Handle email continue (show password step)
  const handleEmailContinue = () => {
    if (!email || !email.includes("@")) {
      triggerNotification({
        type: "warning",
        message: "Invalid Email",
        description: "Please enter a valid email address.",
      });
      return;
    }

    // Show password input step
    setShowPasswordStep(true);
  };

  // Handle back to email step
  const handleBack = () => {
    setShowPasswordStep(false);
    setPassword("");
  };

  // Handle login with email and password
  const handleLogin = async () => {
    if (!password || password.length < 6) {
      triggerNotification({
        type: "warning",
        message: "Invalid Password",
        description: "Please enter your password (minimum 6 characters).",
      });
      return;
    }

    try {
      setIsLoading(true);
      const dataToPost = {
        userId: email,
        password: password,
        otpVerificationRequestId: "",
      };

      const response = await (isAdmin
        ? loginAdmin(dataToPost)
        : loginUser(dataToPost));
      const result = response?.outData;

      if (result.isSuccessful) {
        const loginContext = JSON.stringify({
          userId: dataToPost.userId,
          emailAddress: email,
          ...result.data,
        });

        // Navigate to OTP verification
        navigate("/authentication/otp", {
          state: { loginContext },
        });
      } else {
        triggerNotification({
          type: "error",
          message: "Login Failed",
          description:
            result?.message || "Failed to login. Please check your credentials and try again.",
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Login Error",
        description:
          error?.toString() || "An error occurred. Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const containerStyle = {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: isDark 
      ? "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)"
      : "linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)",
    padding: "20px",
    position: "relative",
  };

  const cardStyle = {
    width: "100%",
    maxWidth: "440px",
    padding: "48px 32px",
    background: isDark ? "#1f1f1f" : "#ffffff",
    borderRadius: "12px",
    boxShadow: isDark
      ? "0 8px 24px rgba(0, 0, 0, 0.4)"
      : "0 8px 24px rgba(0, 0, 0, 0.08)",
  };

  const logoStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "32px",
  };

  const ssoButtonStyle = (provider) => ({
    width: "100%",
    height: "48px",
    borderRadius: "8px",
    border: `1px solid ${isDark ? "#3a3a3a" : "#e0e0e0"}`,
    background: isDark ? "#2a2a2a" : "#ffffff",
    color: isDark ? "#ffffff" : "#262626",
    fontSize: "15px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    cursor: loadingProvider ? "not-allowed" : "pointer",
    opacity: loadingProvider && loadingProvider !== provider ? 0.5 : 1,
    transition: "all 0.3s ease",
    marginBottom: "12px",
  });

  const emailInputStyle = {
    height: "48px",
    borderRadius: "8px",
    fontSize: "15px",
    border: `1px solid ${isDark ? "#3a3a3a" : "#e0e0e0"}`,
    background: isDark ? "#2a2a2a" : "#ffffff",
    color: isDark ? "#ffffff" : "#262626",
  };

  const continueButtonStyle = {
    width: "100%",
    height: "48px",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "600",
    marginTop: "12px",
    background: email ? "#1890ff" : isDark ? "#3a3a3a" : "#f0f0f0",
    color: email ? "#ffffff" : isDark ? "#666666" : "#999999",
    border: "none",
  };

  return (
    <div style={containerStyle}>
      {/* Top Navigation */}
      <div style={{
        position: "absolute",
        top: "20px",
        right: "20px",
        display: "flex",
        gap: "16px",
        alignItems: "center",
        zIndex: 10,
      }}>
        <TenantSwitcher />
        <LanguageSelector />
      </div>

      {/* Sign up link */}
      <div style={{
        position: "absolute",
        top: "20px",
        right: "280px",
        zIndex: 10,
      }}>
        <Button
          type="primary"
          size="large"
          onClick={() => navigate(isAdmin ? "/admin/signup" : "/signup")}
          style={{
            borderRadius: "8px",
            height: "40px",
            fontWeight: "600",
          }}
        >
          Sign up for free
        </Button>
      </div>

      {/* Main Card */}
      <div style={cardStyle}>
        {/* Logo */}
        <div style={logoStyle}>
          <img
            src={tenant?.logoUrl || "/banklingo_logo.svg"}
            alt="BankLingo"
            style={{ height: "40px", objectFit: "contain" }}
          />
        </div>

        {/* Title */}
        <Title
          level={3}
          style={{
            textAlign: "center",
            marginBottom: "32px",
            color: isDark ? "#ffffff" : "#262626",
            fontWeight: "600",
            fontSize: "28px",
          }}
        >
          {isAdmin ? "Log in to BankLingo Admin" : "Log in to BankLingo"}
        </Title>

        {/* Show SSO buttons and email input OR password input */}
        {!showPasswordStep ? (
          <>
            {/* SSO Buttons */}
            <Space direction="vertical" style={{ width: "100%" }} size={0}>
              {Object.entries(SSO_PROVIDERS).map(([key, provider]) => (
                <Button
                  key={key}
                  style={ssoButtonStyle(key)}
                  onClick={() => handleSSOLogin(key)}
                  disabled={loadingProvider !== null}
                  icon={loadingProvider === key ? <Spin size="small" /> : provider.icon}
                >
                  {loadingProvider === key ? "Connecting..." : `Sign in with ${provider.name}`}
                </Button>
              ))}
            </Space>

            {/* Divider */}
            <Divider style={{ 
              margin: "28px 0",
              color: isDark ? "#666666" : "#999999",
              fontSize: "13px",
            }}>
              OR
            </Divider>

            {/* Email Input */}
            <div>
              <Text
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: isDark ? "#a0a0a0" : "#595959",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Work email
              </Text>
              <Input
                size="large"
                placeholder="name@company.com"
                prefix={<MailOutlined style={{ color: isDark ? "#666666" : "#bfbfbf" }} />}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onPressEnter={handleEmailContinue}
                style={emailInputStyle}
              />
              <Button
                type="primary"
                size="large"
                style={continueButtonStyle}
                onClick={handleEmailContinue}
                disabled={!email || !email.includes("@") || isLoading}
              >
                Continue
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Back button */}
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              style={{
                marginBottom: "24px",
                color: isDark ? "#a0a0a0" : "#595959",
                padding: "0",
                height: "auto",
              }}
            >
              Back
            </Button>

            {/* Email display */}
            <div style={{ marginBottom: "24px" }}>
              <Text
                style={{
                  display: "block",
                  fontSize: "14px",
                  color: isDark ? "#a0a0a0" : "#8c8c8c",
                  marginBottom: "4px",
                }}
              >
                Signing in as
              </Text>
              <Text
                strong
                style={{
                  display: "block",
                  fontSize: "16px",
                  color: isDark ? "#ffffff" : "#262626",
                }}
              >
                {email}
              </Text>
            </div>

            {/* Password Input */}
            <div>
              <Text
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: isDark ? "#a0a0a0" : "#595959",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Password
              </Text>
              <Input.Password
                size="large"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onPressEnter={handleLogin}
                style={emailInputStyle}
                iconRender={(visible) =>
                  visible ? (
                    <EyeOutlined style={{ color: isDark ? "#666666" : "#bfbfbf" }} />
                  ) : (
                    <EyeInvisibleOutlined style={{ color: isDark ? "#666666" : "#bfbfbf" }} />
                  )
                }
              />
              
              {/* Forgot password link */}
              <div style={{ textAlign: "right", marginTop: "8px" }}>
                <Link
                  onClick={() => navigate(isAdmin ? "/admin/forgot-password" : "/forgot-password")}
                  style={{ 
                    fontSize: "13px",
                    color: "#1890ff",
                  }}
                >
                  Forgot password?
                </Link>
              </div>

              <Button
                type="primary"
                size="large"
                style={{
                  ...continueButtonStyle,
                  background: password ? "#1890ff" : isDark ? "#3a3a3a" : "#f0f0f0",
                  color: password ? "#ffffff" : isDark ? "#666666" : "#999999",
                }}
                onClick={handleLogin}
                disabled={!password || password.length < 6 || isLoading}
                loading={isLoading}
              >
                {isLoading ? "Signing in..." : "Sign in"}
              </Button>
            </div>
          </>
        )}

        {/* Terms */}
        <Text
          style={{
            display: "block",
            marginTop: "24px",
            textAlign: "center",
            fontSize: "12px",
            color: isDark ? "#666666" : "#8c8c8c",
            lineHeight: "1.6",
          }}
        >
          By signing up, you acknowledge that you have read and understood, and agree to BankLingo's{" "}
          <Link href="/terms" style={{ color: "#1890ff" }}>
            Terms
          </Link>{" "}
          and{" "}
          <Link href="/privacy" style={{ color: "#1890ff" }}>
            Privacy Policy
          </Link>
          .
        </Text>

        {/* Admin/Customer Toggle */}
        {!isAdmin && (
          <div style={{ 
            marginTop: "20px", 
            textAlign: "center",
            paddingTop: "20px",
            borderTop: `1px solid ${isDark ? "#2a2a2a" : "#f0f0f0"}`,
          }}>
            <Text style={{ fontSize: "13px", color: isDark ? "#a0a0a0" : "#8c8c8c" }}>
              Are you an admin?{" "}
              <Link 
                onClick={() => navigate("/admin/sso-login")}
                style={{ color: "#1890ff", fontWeight: "600" }}
              >
                Admin Login
              </Link>
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default SSOLogin;
